import envConfig from 'configs/envConfig';
import i18n, { ReadCallback } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';
import { i18nextPlugin } from 'translation-check';

import enUS from './langs/en-US/translation.json';

export const LANGUAGES = [
  'en',
  'en-US',
  'zh-Hans',
  'zh-CN',
  'zh-SG',
  'zh-HK',
  'zh-TW',
  'es', // spanish
  'it', // italian
  'pt', // portuguese
  'ru', // russian
  'de', // german
  'fr', // french
  'ja' // japanese
];

const multipleKeysLanguageMapping = {
  'zh-CN': 'zh-Hans',
  'zh-SG': 'zh-Hans',
  'zh-HK': 'zh-Hans',
  'zh-TW': 'zh-Hans',
  en: 'en-US'
};

i18n
  // load translation using http -> see /public/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
  // learn more: https://github.com/i18next/i18next-http-backend
  // want your translations to be loaded from a professional CDN? => https://github.com/locize/react-tutorial#step-2---use-the-locize-cdn
  .use(
    resourcesToBackend(
      (language: string, _ns: string, callback: ReadCallback) => {
        const mappedLanguage =
          multipleKeysLanguageMapping[
            language as keyof typeof multipleKeysLanguageMapping
          ] || language;
        if (mappedLanguage === 'en-US') {
          callback(null, enUS);
        } else {
          import(`./langs/${mappedLanguage}/translation.json`)
            .then((m) => callback(null, m.default))
            // `true` marks the error retryable so i18next retries with backoff
            // (the retry may not hit the network if the browser caches a failed import).
            .catch((err) => callback(err, true));
        }
      }
    )
  )
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  .use(i18nextPlugin)
  // init i18next
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    // lng: 'en-US',
    fallbackLng: 'en-US',
    supportedLngs: LANGUAGES,
    debug: envConfig('env') !== 'prod',
    ns: ['translation'],
    resources: { 'en-US': { translation: enUS } },
    partialBundledLanguages: true,
    // retry a failed language chunk twice (~0.9s total) instead of the default 5x (~11s)
    maxRetries: 2,
    retryTimeout: 300,
    interpolation: {
      escapeValue: false // not needed for react as it escapes by default
    }
  });

export default i18n;

export const getLanguage = (languageCode: string) => {
  switch (languageCode) {
    case 'en': // english
      return 'en-US';
    case 'zh': // chinese
      return 'zh-Hans';
    case 'es': // spanish
      return 'es';
    case 'ru': // russian
      return 'ru';
    case 'de': // german
      return 'de';
    case 'fr': // french
      return 'fr';
    case 'pt': // portuguese
      return 'pt';
    case 'it': // italian
      return 'it';
    case 'ja': // japanese
      return 'ja';
    default:
      return 'en-US';
  }
};

export const stripeLocaleMapping = {
  'en-US': 'en',
  'zh-Hans': 'zh',
  'zh-CN': 'zh',
  'zh-SG': 'zh',
  'zh-HK': 'zh',
  'zh-TW': 'zh',
  'de-DE': 'de',
  es: 'es', // spanish
  it: 'it', // italian
  pt: 'pt', // portuguese
  ru: 'ru', // russian
  de: 'de', // german
  fr: 'fr', // french
  ja: 'ja' // japanese
};
